// src/pages/Vendor/VendorLayout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Truck,
  ShoppingBag,
  DollarSign,
  BarChart,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Store,
  AlertCircle,
  ShoppingBagIcon,
  Eye,
  ArrowLeft,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from '../../utils/toast';
import { useVendor } from '../../hooks/useVendor';
import { vendorAPI } from '../../api/vendor';
import type { VendorDashboardStats } from '../../types';

const VendorLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { vendor } = useVendor();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [hasScrolled, setHasScrolled] = useState(false);

  const [stats, setStats] = useState<VendorDashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);


  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    (async () => {
      try {
        const res = await vendorAPI.getDashboardStats();
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) toast.error('Failed to load stats');
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isDesktop]);

  // For swipe detection
  useEffect(() => {
    if (isDesktop) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartElement: HTMLElement | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
      touchStartElement = e.target as HTMLElement;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Skip if swipe started on a scrollable element
      if (touchStartElement) {
        const isScrollable = touchStartElement.closest('.overflow-x-auto, .overflow-auto, .scrollable-table, [role="table"]');
        if (isScrollable) {
          return;
        }
      }

      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      const diffX = touchEndX - touchStartX;
      const diffY = touchEndY - touchStartY;

      // Only if horizontal swipe is more than vertical
      if (Math.abs(diffX) > Math.abs(diffY) * 1.5) {
        if (diffX > 50 && !isMobileMenuOpen) {
          setIsMobileMenuOpen(true);
        } else if (diffX < -50 && isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDesktop, isMobileMenuOpen]);

  const navigation = [
    { name: 'Dashboard', to: '/vendor/dashboard', icon: LayoutDashboard, exact: true },
    { name: 'Products', to: '/vendor/products', icon: Package, badge: stats?.pending_products },
    { name: 'Pickup Requests', to: '/vendor/pickups', icon: Truck, badge: stats?.pending_pickups },
    { name: 'Orders', to: '/vendor/orders', icon: ShoppingBag },
    { name: 'Returns', to: '/vendor/returns', icon: RotateCcw, badge: stats?.pending_returns },
    { name: 'Analytics', to: '/vendor/analytics', icon: BarChart },
    { name: 'Payouts', to: '/vendor/payouts', icon: DollarSign },
    { name: 'Settings', to: '/vendor/settings', icon: Settings },
    { name: 'Switch to Customer View', to: '/products', icon: ArrowLeft }
  ];

  const getPageTitle = () => {
    const path = location.pathname;
    const currentNav = navigation.find(item =>
      item.exact ? path === item.to : path.startsWith(item.to)
    );
    return currentNav?.name || 'Dashboard';
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Sidebar content (shared between desktop and mobile)
  const SidebarContent = () => (
    <>
      {/* Logo area */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
        {(!isCollapsed || !isDesktop) && (
          <div className="flex items-center gap-2">
            <Store className="h-6 w-6 text-blue-600" />
            <span className="text-lg font-bold text-gray-900">Vendor Hub</span>
          </div>
        )}
        {isDesktop && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        )}
        {!isDesktop && (
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Store Info */}
      {(!isCollapsed || !isDesktop) && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <p className="text-sm font-medium text-gray-900 truncate">
            {user?.vendor_profile?.business_name || 'My Store'}
          </p>
          <p className="text-xs text-gray-500">Vendor since {new Date(user?.vendor_profile?.created_at ?? '').getFullYear()}</p>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.to}
            end={item.exact}
            onClick={() => {
              if (!isDesktop) {
                setIsMobileMenuOpen(false);
              }
            }}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 text-sm font-medium transition-colors ${isActive
                ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                : 'text-gray-700 hover:bg-gray-50'
              } ${isCollapsed && isDesktop ? 'justify-center' : 'space-x-3'}`
            }
          >
            <item.icon size={20} />
            {(!isCollapsed || !isDesktop) && (
              <>
                <span>{item.name}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {item.badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-gray-200 p-4 flex-shrink-0">
        <div className={`flex items-center ${isCollapsed && isDesktop ? 'justify-center' : 'space-x-3'}`}>
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-blue-600">
              {user?.first_name?.[0]}{user?.last_name?.[0] || user?.email?.[0]?.toUpperCase() || 'V'}
            </span>
          </div>
          {(!isCollapsed || !isDesktop) && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate capitalize">Vendor</p>
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          className={`mt-4 w-full flex items-center text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg px-3 py-2 transition-colors ${isCollapsed && isDesktop ? 'justify-center' : 'space-x-2'
            }`}
        >
          <LogOut size={18} />
          {(!isCollapsed || !isDesktop) && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 ${isCollapsed ? 'w-20' : 'w-64'
          }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {!isDesktop && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white shadow-2xl z-50 transition-transform duration-300 ease-out lg:hidden
          w-[85vw] max-w-[320px]
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile/Tablet Header - Clean integrated header */}
        <header className={`lg:hidden sticky top-0 z-30 bg-white transition-shadow duration-200 ${hasScrolled ? 'shadow-sm' : ''}`}>
          <div className="flex items-center justify-between h-12 px-4">
            {/* Left: Menu + Logo */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-1.5 -ml-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              {/* <span className="text-sm font-medium text-gray-900">
        {getPageTitle()}
      </span> */}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Shop
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="py-4 px-4 sm:py-6 sm:px-6 lg:px-8">
            <Outlet context={{ stats, statsLoading }} />
          </div>
        </div>

      </main>
    </div>
  );
};

export default VendorLayout;